import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { Icon } from '../../../components/ui/icon.js';
import { cappedMultiplierBp, multiplierBp, payoutMinor } from './multiplier.js';

interface Props {
  bet: bigint;
  minesCount: number;
  currency?: string;
  /** The server's cashout clamp for THIS agent's currency, from the mines config. */
  maxWinMinor: bigint;
}

/**
 * "What if you survive?" ladder. One row per representative safe-reveal count:
 * how many gems → the multiplier → the actual payout on the current bet, so the
 * player can read exactly what the risk is worth before opening a round. The
 * last row is always the full clear (jackpot), highlighted.
 */
export function MinesPayoutPreview({ bet, minesCount, currency, maxWinMinor }: Props) {
  const { t } = useTranslation('originals');
  const { fromMinor } = useMoney(currency);
  const safe = 25 - minesCount;
  const points = [...new Set([1, 3, 5, 10, safe].filter((k) => k >= 1 && k <= safe))]
    .sort((a, b) => a - b)
    .slice(0, 5);
  if (points.length === 0) return null;

  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
          {t('mines.previewTitle')}
        </span>
        <span className="text-[11px] bf-tnum text-[var(--text-muted)]">
          {t('mines.preview.fromBet', { amount: fromMinor(bet) })}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-sunken)]">
        {points.map((k) => {
          const bp = multiplierBp(k, minesCount);
          const m = Number(cappedMultiplierBp(bet, bp, maxWinMinor)) / 10000;
          const jackpot = k === safe && safe > 1;
          return (
            <div
              key={k}
              className={[
                'flex items-center gap-2.5 px-3 py-2 border-t border-[var(--border-subtle,var(--border))] first:border-t-0',
                jackpot ? 'bg-[var(--warning)]/10' : '',
              ].join(' ')}
            >
              <span
                className={[
                  'grid h-6 w-6 shrink-0 place-items-center rounded-md',
                  jackpot ? 'bg-[var(--warning)]/15' : 'bg-[var(--bg-elevated)]',
                ].join(' ')}
              >
                <Icon
                  name={jackpot ? 'star' : 'gem'}
                  size={13}
                  className={jackpot ? 'text-[var(--warning)]' : 'text-[var(--primary)]'}
                />
              </span>
              <span className="min-w-0 truncate text-[12.5px] font-semibold text-[var(--text-secondary)]">
                {jackpot ? t('mines.preview.full') : t('mines.preview.tiles', { count: k })}
              </span>
              <span
                className={[
                  'ml-auto shrink-0 text-[13px] font-extrabold bf-tnum',
                  jackpot ? 'text-[var(--warning)]' : 'text-[var(--primary)]',
                ].join(' ')}
              >
                ×{m.toFixed(2)}
              </span>
              <span className="w-[92px] shrink-0 text-right text-[12.5px] font-bold bf-tnum text-[var(--success)]">
                {fromMinor(payoutMinor(bet, bp, maxWinMinor))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
